import _ from "lodash";
import { useI18n } from "Hooks";
import { connect } from "react-redux";
import classNames from "classnames";
import React, { useMemo, useState } from "react";

// * Internal
import Loading from "UIComponents/Loading";
import { graphql, compose } from "lib/apolloClient";
import { resetAIConversation, updateAIConversation } from "modules/Services";
import { updateWorksheetAIPanel } from "Worksheet/modules/WorksheetModules";
import { createAIConversation } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/AiQuestionAssistantModule";
import { getAIConversationsQuery } from "Worksheet/modules/WorksheetQueries";

// * Relative
import classes from "./WorksheetAIPanel.scss";
import AIPanelHeader from "./AIPanelHeader";
import AIPanelDialogs from "./AIPanelDialogs";
import AIPanelContent from "./AIPanelContent";
import AIPanelContextBar from "./AIPanelContextBar";
import { getWorksheetContextFields } from "./WorksheetAIPanelUtils";
import LearningManager from "../../ExtractFromDoc/LearningManager/LearningManager";

// * Styles
const styles = {
  loaderOverlay: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    display: "flex",
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.75)",
  },
};

/**
 *
 * @param {*} props
 * @returns Worksheet AI Panel Component
 */
const WorksheetAIPanel = props => {
  const {
    isLoading,
    entityType = "ASSESSMENT",
    sourceFrom,
    isPanelOpen,
    assessmentId,
    classSubjects,
    contextOptions,
    conversationId,
    refetchConversation,
    conversationMetaData,

    // * Actions
    resetAIConversation,
    createAIConversation,
    updateAIConversation,
    updateWorksheetAIPanel,
  } = props;

  const { t } = useI18n();
  const [_loading, setLoading] = useState(false);
  const [showContextList, setShowContextList] = useState(true);
  const [dialogData, setDialogData] = useState({ type: null });
  const [inputMessage, setInputMessage] = useState({ value: "" });
  const [context, setContext] = useState({});
  const [learningGoalFieldsData, setLearningGoalFieldsData] = useState([]);

  // * Loading state
  const loading = isLoading || _loading || !entityType;

  /**
   *
   * @param {*} action
   * @param {*} payload
   * @description
   * onEvent handles the events triggered from the AI panel.
   */
  const onEvent = async (action, payload) => {
    switch (action) {
      case "CLOSE_PANEL":
        updateWorksheetAIPanel({ isOpen: false });
        break;

      case "CLEAR_CHAT":
        setDialogData({
          type: "CLEAR_CHAT",
          buttonText: t("common:clear"),
          title: t("classRoom:clear_chat"),
          body: t("classRoom:clear_chat_history_msg"),
        });
        break;

      case "SEND_MESSAGE":
        alert("Message sent!");
        break;

      case "TOGGLE_CONTEXT_LIST":
        setShowContextList(prev => !prev);
        break;

      case "SWITCH_CONTEXT": {
        setLoading(true);
        const hasConversation = _.get(payload, "isConversationCreated");
        if (!hasConversation) {
          const response = await createAIConversation({
            input: {
              metadata: {},
              entityType: "QUESTION",
              entityId: _.get(payload, "questionId"),
            },
            isQuestionAssistant: true,
          });
          updateWorksheetAIPanel({ conversationId: _.get(response, "id") });
        } else {
          updateWorksheetAIPanel({ conversationId: _.get(payload, "key") });
        }
        setLoading(false);
        break;
      }

      case "CONTEXT_ITEM_CLICK":
        alert(`Context item clicked: ${_.get(payload, "id")}`);
        break;

      default:
        break;
    }
  };

  /**
   *
   * @param {*} action
   * @param {*} payload
   * @description
   * handleDialogSubmit handles the submission of dialog actions.
   */
  const handleDialogSubmit = async (action, payload) => {
    switch (action) {
      case "CLEAR_CHAT":
        setLoading(true);
        setDialogData({ type: null });
        await resetAIConversation({ input: { id: conversationId } });
        await updateAIConversation({
          input: { id: conversationId, metadata: {} },
        });
        await refetchConversation();
        setLoading(false);
        break;
      default:
        break;
    }

    setDialogData({ type: null });
  };

  const contextFields = useMemo(
    () => getWorksheetContextFields({ t, data: {}, entityType }),
    [entityType]
  );

  const onUpdateLearningGoals = ({ newLearningGoalsFieldData }) => {
    setLearningGoalFieldsData(newLearningGoalsFieldData);
  };

  if (!conversationId) return null;

  const aiPanelContainerClass = classNames(classes.worksheetAIPanelContainer, {
    [classes.worksheetAIPanelVisible]: isPanelOpen,
  });

  return (
    <>
      <div className={aiPanelContainerClass}>
        <AIPanelHeader
          sendEvent={onEvent}
          showClearChat={true}
          title="Worksheet Assistant"
          conversationId={conversationId}
          testId="worksheet-ai-panel-header"
        />

        <AIPanelContent testId="worksheet-ai-panel-content" />

        <AIPanelContextBar
          sendEvent={onEvent}
          entityType={entityType}
          inputMessage={inputMessage}
          contextFields={contextFields}
          conversationId={conversationId}
          contextOptions={contextOptions}
          showContextList={showContextList}
          setInputMessage={setInputMessage}
          testId="worksheet-ai-panel-context-bar"
          conversationMetaData={conversationMetaData}
          renderMoreContextFields={(renderTitle, renderValue) =>
            showContextList && (
              <LearningManager
                context={context}
                setContext={setContext}
                sourceFrom={sourceFrom}
                renderTitle={renderTitle}
                renderValue={renderValue}
                assessmentId={assessmentId}
                classSubjects={classSubjects}
                isFromWorksheetAIPanel={true}
                onChangeLearningGoals={onUpdateLearningGoals}
              />
            )
          }
        />

        {loading && <Loading containerStyle={styles.loaderOverlay} />}
      </div>

      <AIPanelDialogs
        {...dialogData}
        onSubmit={handleDialogSubmit}
        onClose={() => setDialogData({ type: null })}
      />
    </>
  );
};

const mapStateToProps = state => {
  const portalType = _.get(state, "login.userInfo.portalType");
  const isPanelOpen = _.get(state, "worksheet.aiPanel.isOpen", false);
  const conversationId = _.get(state, "worksheet.aiPanel.conversationId", null);

  return { portalType, isPanelOpen, conversationId };
};

const mapDispatchToProps = {
  resetAIConversation,
  updateAIConversation,
  createAIConversation,
  updateWorksheetAIPanel,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ conversationId }) => _.isEmpty(conversationId),
    options: ({ conversationId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: conversationId, portalType },
      context: { useHttpLink: true },
    }),
    props({ getAIConversations }) {
      const node = _.get(getAIConversations, "node");
      const entityType = _.get(node, "entityType", "ASSESSMENT");
      const conversationMessages = _.get(node, "messages", {});
      const conversationMetaData = _.get(node, "metadata", {});
      const refetchConversation = getAIConversations?.refetch || _.noop;

      return {
        entityType,
        refetchConversation,
        conversationMessages,
        conversationMetaData,
        isLoading: getAIConversations?.loading,
      };
    },
  })
)(WorksheetAIPanel);
